/**
 * CR340: Deal Cards Debug Preview Panel
 * 
 * Compact debug panel showing resolved config, deal summary,
 * save trace, and diagnostics export for fast triage.
 */

import { useState, useMemo, useCallback } from "react";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from "@/components/ui/collapsible";
import { ScrollArea } from "@/components/ui/scroll-area";
import { ChevronDown, ChevronRight, Copy, Check, Bug, AlertCircle } from "lucide-react";
import { cn } from "@/lib/utils";
import { toast } from "sonner";
import type { SaveTrace } from "@/lib/diagnostics/saveTrace";

// ================================================================
// TYPES
// ================================================================

interface DealCardsDebugPanelProps {
  dealTypeKey: string;
  variantKey?: string;
  selectedDeal?: Record<string, unknown> | null;
  savedTemplate?: Record<string, unknown> | null;
  savedComponents?: Record<string, unknown> | null;
  editingDraft?: Record<string, unknown> | null;
  appliedDraft?: Record<string, unknown> | null;
  lastSaveTrace?: SaveTrace | null;
  previewState?: "disabled" | "saved" | "applied";
  className?: string;
}

// ================================================================
// COMPONENT
// ================================================================

export function DealCardsDebugPanel({
  dealTypeKey,
  variantKey = "standard_v2",
  selectedDeal,
  savedTemplate,
  savedComponents,
  editingDraft,
  appliedDraft,
  lastSaveTrace,
  previewState = "saved",
  className,
}: DealCardsDebugPanelProps) {
  const [dealOpen, setDealOpen] = useState(false);
  const [configOpen, setConfigOpen] = useState(false);
  const [traceOpen, setTraceOpen] = useState(true);
  const [copied, setCopied] = useState(false);

  // Build diagnostics JSON
  const diagnosticsPayload = useMemo(() => {
    const payload: Record<string, unknown> = {
      capturedAt: new Date().toISOString(),
      tab: "components",
      dealTypeKey,
      variantKey,
      dataSource: previewState,
      selectedDeal: selectedDeal ? {
        id: selectedDeal.id,
        merchant_name: selectedDeal.merchant_name,
        title: selectedDeal.title || selectedDeal.headline_mechanic,
        deal_type: selectedDeal.deal_type,
      } : null,
      saved: {
        template_keys: savedTemplate ? Object.keys(savedTemplate) : null,
        components_keys: savedComponents ? Object.keys(savedComponents) : null,
      },
      drafts: {
        componentsEditingDraft: editingDraft ? Object.keys(editingDraft) : null,
        componentsAppliedDraft: appliedDraft ? Object.keys(appliedDraft) : null,
      },
      lastSaveTrace: lastSaveTrace ?? null,
    };

    // Size guard: omit raw deal if payload too large
    const json = JSON.stringify(payload);
    if (json.length > 500_000) {
      payload.selectedDeal = { _truncated: true, id: selectedDeal?.id };
    }

    return payload;
  }, [dealTypeKey, variantKey, previewState, selectedDeal, savedTemplate, savedComponents, editingDraft, appliedDraft, lastSaveTrace]);

  const handleCopy = useCallback(async () => {
    const json = JSON.stringify(diagnosticsPayload, null, 2);
    try {
      await navigator.clipboard.writeText(json);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
      toast.success("Diagnostics copied to clipboard");
    } catch {
      // Fallback: log to console
      console.log("[CR340] Diagnostics:", json);
      toast.info("Diagnostics logged to console (clipboard unavailable)");
    }
  }, [diagnosticsPayload]);

  return (
    <div className={cn("border rounded-lg bg-muted/30 overflow-hidden text-xs", className)}>
      {/* Header */}
      <div className="flex items-center justify-between px-3 py-2 border-b bg-muted/50">
        <div className="flex items-center gap-2">
          <Bug className="h-3.5 w-3.5 text-muted-foreground" />
          <span className="font-medium text-sm">Debug Preview</span>
          <Badge variant="outline" className="text-[10px] px-1.5 py-0 h-5">
            {dealTypeKey}
          </Badge>
          <Badge 
            variant={previewState === "applied" ? "default" : "secondary"} 
            className="text-[10px] px-1.5 py-0 h-5"
          >
            Preview: {previewState}
          </Badge>
        </div>
        <Button variant="ghost" size="sm" onClick={handleCopy} className="h-7 px-2 text-xs gap-1">
          {copied ? <Check className="h-3 w-3" /> : <Copy className="h-3 w-3" />}
          {copied ? "Copied" : "Copy Diagnostics"}
        </Button>
      </div>

      <ScrollArea className="max-h-[300px]">
        <div className="p-3 space-y-2">
          {/* Selected Deal Summary */}
          <DebugSection title="Selected Deal" open={dealOpen} onToggle={setDealOpen}>
            {selectedDeal ? (
              <div className="space-y-1">
                <KV label="ID" value={String(selectedDeal.id ?? "—").slice(0, 12)} />
                <KV label="Merchant" value={String(selectedDeal.merchant_name ?? "—")} />
                <KV label="Title" value={String(selectedDeal.title ?? selectedDeal.headline_mechanic ?? "—")} />
                <KV label="Type" value={String(selectedDeal.deal_type ?? "—")} />
              </div>
            ) : (
              <span className="text-muted-foreground italic">No deal selected</span>
            )}
          </DebugSection>

          {/* Config Sources */}
          <DebugSection title="Config Sources" open={configOpen} onToggle={setConfigOpen}>
            <div className="space-y-1">
              <KV label="Saved Template" value={savedTemplate ? `${Object.keys(savedTemplate).length} keys` : "—"} />
              <KV label="Saved Components" value={savedComponents ? `${Object.keys(savedComponents).length} keys` : "—"} />
              <KV label="Editing Draft" value={editingDraft ? `${Object.keys(editingDraft).length} keys` : "—"} />
              <KV label="Applied Draft" value={appliedDraft ? `${Object.keys(appliedDraft).length} keys` : "same as saved"} />
            </div>
          </DebugSection>

          {/* Last Save Trace */}
          <DebugSection title="Last Save" open={traceOpen} onToggle={setTraceOpen}>
            {lastSaveTrace ? (
              <div className="space-y-1">
                <KV label="Action" value={lastSaveTrace.action} />
                <KV 
                  label="Status" 
                  value={lastSaveTrace.status} 
                  status={lastSaveTrace.status === "success" ? "ok" : lastSaveTrace.status === "error" ? "error" : "neutral"} 
                />
                <KV label="Keys" value={lastSaveTrace.payloadTopKeys.join(", ") || "—"} />
                <KV label="Time" value={new Date(lastSaveTrace.timestamp).toLocaleTimeString()} />
                {lastSaveTrace.status === "success" && lastSaveTrace.responseSnippet && (
                  <div className="mt-1 p-1.5 bg-green-50 border border-green-200 rounded text-[10px] font-mono text-green-800 truncate">
                    {lastSaveTrace.responseSnippet.slice(0, 120)}
                  </div>
                )}
                {lastSaveTrace.errorMessage && (
                  <div className="mt-1 p-1.5 bg-destructive/10 border border-destructive/30 rounded text-destructive text-[10px]">
                    <AlertCircle className="h-3 w-3 inline mr-1" />
                    {lastSaveTrace.errorMessage}
                  </div>
                )}

                {/* CR341: Save Checklist */}
                <div className="mt-2 pt-2 border-t space-y-0.5">
                  <p className="text-[10px] font-medium text-muted-foreground uppercase tracking-wide mb-1">Save Checklist</p>
                  <CheckItem label="Save fired" pass={true} />
                  <CheckItem label="API responded" pass={lastSaveTrace.status !== "pending"} />
                  <CheckItem label="Status OK" pass={lastSaveTrace.status === "success"} />
                  <CheckItem label="No error" pass={!lastSaveTrace.errorMessage} />
                </div>
              </div>
            ) : (
              <span className="text-muted-foreground italic">No saves yet this session</span>
            )}
          </DebugSection>
        </div>
      </ScrollArea>
    </div>
  );
}

// ================================================================
// HELPERS
// ================================================================

function DebugSection({ title, open, onToggle, children }: {
  title: string;
  open: boolean;
  onToggle: (open: boolean) => void;
  children: React.ReactNode;
}) {
  return (
    <Collapsible open={open} onOpenChange={onToggle}>
      <CollapsibleTrigger className="flex items-center gap-1.5 w-full text-left py-1 text-xs font-medium text-muted-foreground hover:text-foreground transition-colors">
        {open ? <ChevronDown className="h-3 w-3" /> : <ChevronRight className="h-3 w-3" />}
        {title}
      </CollapsibleTrigger>
      <CollapsibleContent className="pl-4 pt-1">
        {children}
      </CollapsibleContent>
    </Collapsible>
  );
}

function KV({ label, value, status }: { label: string; value: string; status?: "ok" | "error" | "neutral" }) {
  return (
    <div className="flex items-baseline gap-2">
      <span className="text-muted-foreground min-w-[100px] flex-shrink-0">{label}:</span>
      <span className={cn(
        "font-mono break-all",
        status === "ok" && "text-green-600",
        status === "error" && "text-destructive",
      )}>
        {value}
      </span>
    </div>
  );
}

function CheckItem({ label, pass }: { label: string; pass: boolean }) {
  return (
    <div className="flex items-center gap-1.5 text-[10px]">
      <span className={pass ? "text-green-600" : "text-destructive"}>{pass ? "✓" : "✗"}</span>
      <span className={pass ? "text-muted-foreground" : "text-destructive font-medium"}>{label}</span>
    </div>
  );
}

export default DealCardsDebugPanel;
